// Reglas de estado compartidas por las vistas del Portal Tickets: cuándo una etapa ya terminó
// el trabajo y cuándo un ticket abierto se considera vencido.
import { format, formatDistanceStrict } from "date-fns";
import { es } from "date-fns/locale";
import type { Etapa, Priority, Ticket } from "./tickets-data";

const TIPOS_TERMINALES = new Set(["resuelta", "cerrada", "descartada"]);
// Respaldo por nombre para pipelines dados de alta después de la migración que derivó
// `tipo_semantico` (App Agentes, Nuevos Features, Soporte datos cliente lo tienen en NULL).
const NOMBRE_TERMINAL = /resuelt|cerrad|conclu|finaliz|no proced|cancelad|rechazad|desist/i;
/** Terminales que descartan el caso en vez de resolverlo. */
const NOMBRE_DESCARTE = /no proced|cancelad|rechazad|desist|descart/i;

/**
 * Etapa donde el ticket ya no requiere trabajo: las que cierran el ticket (`cerrada`) y las
 * de "Resuelto" que esperan confirmación. `cerrada` sola no basta: en App Clientes, Bottura y
 * Daiku "Resuelto" es `cerrada = false` y contaba como abierto en KPIs y en el Equipo.
 */
export function esEtapaTerminal(etapa: Pick<Etapa, "cerrada" | "nombre" | "tipoSemantico"> | undefined): boolean {
  if (!etapa) return false;
  if (etapa.cerrada) return true;
  if (etapa.tipoSemantico) return TIPOS_TERMINALES.has(etapa.tipoSemantico);
  return NOMBRE_TERMINAL.test(etapa.nombre);
}

/** Ids de las etapas terminales, para filtrar listas grandes sin buscar etapa por ticket. */
export function idsEtapasTerminales(etapas: Etapa[]): Set<string> {
  return new Set(etapas.filter(esEtapaTerminal).map((e) => e.id));
}

/**
 * Días HÁBILES que puede estar abierto un ticket antes de contar como vencido, por prioridad.
 * La fecha real la calcula la BD (`tickets.fecha_vencimiento`, horario 10:00-18:00 lun-vie CDMX);
 * estos plazos son el espejo para los textos y el respaldo (días naturales) en ambientes sin la
 * migración. Si cambian, cambian también en la función de BD.
 */
export const DIAS_PARA_VENCER: Record<Priority, number> = {
  alta: 2,
  media: 5,
  baja: 10,
  sin: 10,
};

/** Desde cuándo corre el reloj del ticket: entrada a su etapa actual, o su creación si no hay dato. */
export function inicioEtapa(t: Pick<Ticket, "fechaCreacion" | "fechaEtapa">): string {
  return t.fechaEtapa || t.fechaCreacion;
}

/** Días completos que lleva el ticket en su etapa actual (respaldo: desde su creación). */
export function diasEnEtapa(t: Pick<Ticket, "fechaCreacion" | "fechaEtapa">, ahora = Date.now()): number {
  return Math.floor((ahora - new Date(inicioEtapa(t)).getTime()) / 86_400_000);
}

/** Texto corto del tiempo en la etapa actual: «Hoy en esta etapa», «3 días en esta etapa». */
export function antiguedadEnEtapa(t: Pick<Ticket, "fechaCreacion" | "fechaEtapa">, ahora = Date.now()): string {
  const dias = Math.max(0, diasEnEtapa(t, ahora));
  if (dias < 1) return "Hoy en esta etapa";
  if (dias < 30) return `${dias} día${dias === 1 ? "" : "s"} en esta etapa`;
  const meses = Math.floor(dias / 30);
  if (meses < 12) return `${meses} mes${meses === 1 ? "" : "es"} en esta etapa`;
  const anios = Math.floor(meses / 12);
  return `${anios === 1 ? "Un año" : `${anios} años`} en esta etapa`;
}

const NOMBRE_ESPERA = /espera/i;
const TIPOS_SISTEMA = new Set(["sistema", "cambio_estado"]);

/**
 * Último mensaje visible al cliente: del equipo (autor = usuario del portal) o del cliente
 * (la app lo registra con `tipo = comentario` y sin autor). Filas del sistema no cuentan.
 */
function ultimoMensajeEsDelEquipo(actividad: Ticket["actividad"] | undefined): boolean {
  let ultimo: { fecha: number; equipo: boolean } | null = null;
  for (const a of actividad ?? []) {
    if (a.tipo && TIPOS_SISTEMA.has(a.tipo)) continue;
    const equipo = !!a.autorId;
    if (!equipo && a.tipo !== "comentario") continue; // sin autor y que no es del cliente: sistema
    if (equipo && !a.visibleCliente) continue; // nota interna del equipo
    const fecha = new Date(a.fecha).getTime();
    if (!ultimo || fecha >= ultimo.fecha) ultimo = { fecha, equipo };
  }
  return !!ultimo?.equipo;
}

/**
 * El ticket espera respuesta del cliente: no es terminal y su etapa es de espera («En espera»,
 * «Esperando cliente») o el último mensaje visible al cliente es del equipo y él no ha contestado.
 * Un ticket así no cuenta como vencido para el equipo.
 */
export function esperandoCliente(
  t: Pick<Ticket, "etapaId" | "actividad">,
  etapas: Pick<Etapa, "id" | "cerrada" | "nombre" | "tipoSemantico">[],
): boolean {
  const etapa = etapas.find((e) => e.id === t.etapaId);
  if (esEtapaTerminal(etapa)) return false;
  if (etapa && NOMBRE_ESPERA.test(etapa.nombre)) return true;
  return ultimoMensajeEsDelEquipo(t.actividad);
}

/**
 * Abierto (no en etapa terminal), sin esperar al cliente y pasado de su `fechaVencimiento`
 * (horas hábiles, calculada en BD). Si el ticket no trae fecha (ambiente sin la migración) se usa
 * la regla vieja: más días naturales en su etapa de los que permite su prioridad.
 * Sin `etapas` solo se aplica la parte de terminales.
 */
export function estaVencido(
  t: Pick<Ticket, "fechaCreacion" | "fechaEtapa" | "prioridad" | "etapaId"> &
    Partial<Pick<Ticket, "actividad" | "fechaVencimiento">>,
  terminales: Set<string>,
  ahora = Date.now(),
  etapas: Pick<Etapa, "id" | "cerrada" | "nombre" | "tipoSemantico">[] = [],
): boolean {
  if (terminales.has(t.etapaId)) return false;
  if (etapas.length && esperandoCliente({ etapaId: t.etapaId, actividad: t.actividad ?? [] }, etapas)) return false;
  if (t.fechaVencimiento) return ahora > new Date(t.fechaVencimiento).getTime();
  return diasEnEtapa(t, ahora) > DIAS_PARA_VENCER[t.prioridad];
}

export const TEXTO_REGLA_VENCIDO = `Pasó su fecha de vencimiento: ${DIAS_PARA_VENCER.alta} días hábiles con prioridad alta, ${DIAS_PARA_VENCER.media} con media o ${DIAS_PARA_VENCER.baja} con baja o sin prioridad. Se cuenta de lunes a viernes de 10:00 a 18:00 (hora CDMX, sin festivos); lo levantado desde las 16:00, en fin de semana o festivo empieza el siguiente día hábil a las 10:00. El reloj se reinicia una sola vez, al entrar a atención. Los que esperan respuesta del cliente no vencen`;

/** Fecha y hora de vencimiento en es-MX: «lun 26 oct 2026, 15:30». */
export function fechaVencimientoTexto(fecha: string): string {
  return format(new Date(fecha), "EEE d MMM yyyy, HH:mm", { locale: es });
}

/**
 * Línea de vencimiento de un ticket abierto: «Vence el lun 26 oct 2026, 15:30» o
 * «Venció hace 3 días». Sin fecha (ambiente sin la migración) devuelve null.
 */
export function textoVencimiento(
  t: Pick<Ticket, "fechaVencimiento">,
  ahora = Date.now(),
): string | null {
  if (!t.fechaVencimiento) return null;
  const vence = new Date(t.fechaVencimiento);
  if (ahora > vence.getTime()) {
    return `Venció hace ${formatDistanceStrict(vence, ahora, { locale: es })} (${fechaVencimientoTexto(t.fechaVencimiento)})`;
  }
  return `Vence el ${fechaVencimientoTexto(t.fechaVencimiento)}`;
}

// ─── Pendientes por pipeline ────────────────────────────────────────────────────

/**
 * Color de texto del número de pendientes: verde sin pendientes, ámbar de 1 a 5, rojo más de 5.
 * Mismos tonos que los KPIs del portal (Resueltos, Sin asignar, Vencidos).
 */
export function clasePendientes(n: number): string {
  if (n <= 0) return "text-emerald-600";
  if (n <= 5) return "text-amber-600";
  return "text-red-600";
}

export const textoPendientes = (n: number) => (n === 1 ? "1 pendiente" : `${n} pendientes`);

// ─── Sin retroceso de etapa ─────────────────────────────────────────────────────

export const TEXTO_NO_RETROCEDE = "No se puede regresar a una etapa anterior";
export const TOAST_NO_RETROCEDE = "Un ticket no puede regresar a una etapa anterior";

type EtapaMovible = Pick<Etapa, "id" | "pipelineId" | "orden" | "cerrada" | "nombre" | "tipoSemantico">;

/**
 * ¿Se puede mover un ticket de `origen` a `destino`? Misma regla que el trigger de BD
 * (`ticket_no_retrocede`): ir a una etapa terminal siempre se puede; salir de una terminal hacia
 * una no terminal, o ir a una no terminal de orden menor, no. Cambiar de pipeline se permite.
 */
export function puedeMoverA(origen: EtapaMovible | undefined, destino: EtapaMovible | undefined): boolean {
  if (!destino) return false;
  if (!origen || origen.id === destino.id) return true;
  if (esEtapaTerminal(destino)) return true;
  if (origen.pipelineId !== destino.pipelineId) return true;
  if (esEtapaTerminal(origen)) return false;
  return destino.orden >= origen.orden;
}

/** Error del trigger que impide retroceder (el mensaje del motor empieza con la clave). */
export function esErrorNoRetrocede(error: { message?: string | null } | null | undefined): boolean {
  return !!error?.message && error.message.trimStart().startsWith("ticket_no_retrocede");
}

// ─── Ticket relacionado (ticket #1360) ──────────────────────────────────────────

/** Folio escrito por la persona («1360», «#1360»): entero positivo, o null si no es un folio. */
export function folioCapturado(texto: string): number | null {
  const limpio = texto.trim().replace(/^#\s*/, "");
  if (!/^\d+$/.test(limpio)) return null;
  const n = Number(limpio);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Ticket anterior que se quiere relacionar, tal como se lee de la BD. */
export type TicketReferido = {
  id: string;
  numero: number;
  nombre: string;
  activo: boolean;
  /** Su etapa tiene `cerrada` (la que «Cierra el ticket»): solo para mostrarlo. */
  cerrado: boolean;
  etapa: string;
  /** Para la vista previa al relacionarlo (opcionales: quien arma el objeto puede omitirlos). */
  descripcion?: string | null;
  solicitante?: string | null;
  inmueble?: string | null;
  pipelineId?: string | null;
  fechaCreacion?: string | null;
  fechaCierre?: string | null;
};

/**
 * Por qué no se puede relacionar el ticket (null = sí se puede). Basta con que exista, esté activo
 * y no sea el mismo ticket (`propioId`): abierto o cerrado da igual. Misma regla que el trigger
 * `trg_tickets_ticket_relacionado_cerrado` de la BD.
 */
export function motivoTicketRelacionadoInvalido(
  folio: number | null,
  ticket: TicketReferido | null,
  propioId?: string,
): string | null {
  if (folio === null) return "Escribe el folio del ticket, solo números (ej. 1360).";
  if (!ticket || !ticket.activo) return `No existe el ticket #${folio}.`;
  if (propioId && ticket.id === propioId) return "Un ticket no se puede relacionar consigo mismo.";
  return null;
}

/**
 * Error del trigger que rechaza la relación. El mensaje empieza con la clave: la de ahora
 * (`ticket_relacionado_invalido`) o la de antes (`ticket_relacionado_no_cerrado`).
 */
const RE_ERROR_RELACIONADO = /^\s*ticket_relacionado_(?:invalido|no_cerrado):\s*/;

export function esErrorTicketRelacionado(error: { message?: string | null } | null | undefined): boolean {
  return !!error?.message && RE_ERROR_RELACIONADO.test(error.message);
}

/** Texto para la persona: el motivo del trigger sin su clave, o el error tal cual. */
export function mensajeErrorTicketRelacionado(error: { message?: string | null }): string {
  return (error.message ?? "").replace(RE_ERROR_RELACIONADO, "");
}

/**
 * Otro ticket ligado por `tickets.id_ticket_relacionado`: el anterior al que apunta este ticket o
 * uno posterior que apunta a este. Al resolver uno se resuelven también los relacionados abiertos
 * (un nivel: los directos).
 */
export type TicketRelacionado = {
  id: string;
  numero: number;
  nombre: string;
  pipelineId: string;
  etapaId: string;
  etapa: string;
  /** Ya está en una etapa terminal: no se toca al resolver el otro. */
  terminal: boolean;
  relacion: "anterior" | "posterior";
};

/**
 * Etapa a la que pasa un relacionado cuando se resuelve el principal hacia `destino`: en el
 * pipeline del relacionado, la terminal con el mismo `tipo_semantico`; si no hay, la del mismo
 * nombre; si no, la terminal con el mismo `cerrada`; si no, la primera terminal. Los respaldos
 * no caen en una de descarte («No procede», «Cancelado»): resolver no es descartar. undefined =
 * su pipeline no tiene etapa terminal a la que pasarlo.
 */
export function etapaCierreEquivalente(destino: Etapa, pipelineId: string, etapas: Etapa[]): Etapa | undefined {
  const terminales = etapas
    .filter((e) => e.pipelineId === pipelineId && esEtapaTerminal(e))
    .sort((a, b) => a.orden - b.orden);
  const exacta =
    (destino.tipoSemantico ? terminales.find((e) => e.tipoSemantico === destino.tipoSemantico) : undefined) ??
    terminales.find((e) => sinAcentos(e.nombre) === sinAcentos(destino.nombre));
  if (exacta) return exacta;
  const deResolucion = terminales.filter((e) => e.tipoSemantico !== "descartada" && !NOMBRE_DESCARTE.test(e.nombre));
  return deResolucion.find((e) => e.cerrada === destino.cerrada) ?? deResolucion[0];
}

// ─── «Yo trabajo el ticket» ─────────────────────────────────────────────────────

const sinAcentos = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Etapa a la que pasa un ticket cuando alguien lo toma: «En atención» si el pipeline la tiene;
 * si no, «En proceso» o la primera etapa de trabajo (ni abierta ni terminal).
 */
export function etapaAtencion(etapas: Etapa[], pipelineId: string): Etapa | undefined {
  const delPipeline = etapas
    .filter((e) => e.pipelineId === pipelineId && !esEtapaTerminal(e))
    .sort((a, b) => a.orden - b.orden);
  return (
    delPipeline.find((e) => sinAcentos(e.nombre).includes("atencion")) ??
    delPipeline.find((e) => sinAcentos(e.nombre).includes("en proceso")) ??
    delPipeline.find((e) => e.tipoSemantico === "en_proceso") ??
    delPipeline[1]
  );
}

/** Etapa de entrada del pipeline (la primera, o la de tipo «abierta»). */
export function etapaEntrada(etapas: Etapa[], pipelineId: string): Etapa | undefined {
  const delPipeline = etapas.filter((e) => e.pipelineId === pipelineId).sort((a, b) => a.orden - b.orden);
  return delPipeline.find((e) => e.tipoSemantico === "abierta") ?? delPipeline[0];
}

/**
 * Quién trabaja el ticket: su único propietario, si el ticket está en la etapa de atención.
 * Con varios propietarios o en otra etapa, nadie lo tiene tomado.
 */
export function atendidoPor(t: Pick<Ticket, "propietarios" | "etapaId" | "pipelineId">, etapas: Etapa[]): string | null {
  if (t.propietarios.length !== 1) return null;
  return etapaAtencion(etapas, t.pipelineId)?.id === t.etapaId ? t.propietarios[0] : null;
}
